<template>
  <!-- Three render paths, and not one of them can produce a white box.
       The rule is enforced by this component, not by remembering. -->
  <div
    v-if="shape === 'mono'"
    class="mark mark--mono"
    :style="{ width: px, height: px, background: gradient, fontSize: monoSize }"
    aria-hidden="true"
  >
    {{ project.monogram }}
  </div>

  <img
    v-else
    class="mark"
    :class="shape === 'tile' ? 'mark--tile' : 'mark--free'"
    :src="src"
    :alt="alt"
    :aria-hidden="alt ? undefined : 'true'"
    :width="size"
    :height="size"
    loading="lazy"
    decoding="async"
  />
</template>

<script>
import { computed } from "vue";
import { assetUrl, gradientOf } from "@/composables/useProjectAssets";

export default {
  name: "ProjectMark",
  props: {
    project: { type: Object, required: true },
    size: { type: Number, default: 44 },
    /**
     * Marks sitting next to their own visible title are decorative — an
     * empty alt avoids a screen reader announcing the name twice. Pass a
     * string only where the mark stands alone.
     */
    alt: { type: String, default: "" },
  },
  setup(props) {
    const shape = computed(() => props.project.markShape || "tile");
    const src = computed(() => assetUrl(props.project.mark || props.project.thumbnail));
    const gradient = computed(() => gradientOf(props.project));
    const px = computed(() => `${props.size}px`);
    const monoSize = computed(() => `${Math.round(props.size * 0.44)}px`);
    return { shape, src, gradient, px, monoSize };
  },
};
</script>

<style scoped>
.mark {
  flex-shrink: 0;
  object-fit: contain;
}

/* A real app tile. The hairline ring exists for exactly one asset:
   dashbuddy-icon.png is opaque with white corners, and without a ring it
   would be an invisible-edged white square on the near-white ground — the
   banned white frame, by accident. */
.mark--tile {
  border-radius: var(--r-tile);
  box-shadow: 0 0 0 var(--hair-w) rgba(29, 29, 31, 0.08), var(--sh-1);
  background: none;
  padding: 0;
}

/* An alpha silhouette. The shadow follows the shape, so there is no plate
   behind it to become a frame. */
.mark--free {
  border-radius: 0;
  background: none;
  padding: 0;
  filter: drop-shadow(0 6px 14px rgba(29, 29, 31, 0.18));
}

/* No square mark exists for this project, so we draw one from its own
   colours rather than stretching a wordmark or falling back to white. */
.mark--mono {
  display: grid;
  place-items: center;
  border-radius: var(--r-tile);
  color: rgba(255, 255, 255, 0.92);
  font-family: var(--font-sans);
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1;
  box-shadow: var(--sh-1);
  user-select: none;
}
</style>
